import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { AppleButton } from "@/components/ui/apple-button";
import { Loader2, Calendar, Phone, Tag, Box, Building2, MapPin, Clock, FileText, DollarSign, User, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Database } from "@/integrations/supabase/types";
import { AssetRecordsList } from "@/components/assets/AssetRecordsList";
import RegisterAssetModal from "@/components/ui/RegisterAssetModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AssetRecordForm } from "@/components/forms/AssetRecordForm";

type Asset = Database['public']['Tables']['assets']['Row'] & {
  asset_records: Database['public']['Tables']['asset_records']['Row'][];
};

interface NFCScannerProps {
  onScan: (id: string) => void;
}

export function NFCScanner({ onScan }: NFCScannerProps) {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nfcId, setNfcId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [asset, setAsset] = useState<Asset | null>(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showRecordDialog, setShowRecordDialog] = useState(false);
  const { toast } = useToast();
  const abortControllerRef = useRef<AbortController | null>(null);
  const ndefReaderRef = useRef<any>(null);

  const findAssetByNfcId = async (id: string) => {
    if (!id.trim()) {
      toast({
        variant: "destructive",
        title: "Invalid NFC ID",
        description: "NFC ID cannot be empty"
      });
      return;
    }

    setIsLoading(true);
    setAsset(null);
    try {
      const { data, error } = await supabase
        .from("assets")
        .select(`
          *,
          asset_records(
            *
          )
        `)
        .eq("nfc_id", id)
        .single();

      if (error || (data && data.private === true)) {
        setShowRegisterModal(true);
        return;
      }

      setAsset(data as Asset);
      
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error finding asset",
        description: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  const startScanning = async () => {
    if (!('NDEFReader' in window)) {
      setError('NFC is not supported on this device');
      return;
    }

    try {
      setIsScanning(true);
      setError(null);
      setNfcId(null);
      setAsset(null);

      // Create new AbortController
      abortControllerRef.current = new AbortController();
      ndefReaderRef.current = new (window as any).NDEFReader();

      await ndefReaderRef.current.scan({
        signal: abortControllerRef.current.signal,
        recordType: "text",
        mediaType: "text/plain",
      });

      ndefReaderRef.current.addEventListener("reading", async ({ serialNumber }: { serialNumber: string }) => {
        setNfcId(serialNumber);
        onScan(serialNumber);
        setIsScanning(false);
        await findAssetByNfcId(serialNumber);
      });

      ndefReaderRef.current.addEventListener("error", (error: Error) => {
        setError(error.message);
        setIsScanning(false);
      });

    } catch (error: any) {
      setError(error.message || 'Failed to start NFC scanning');
      setIsScanning(false);
    }
  };

  const stopScanning = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    ndefReaderRef.current = null;
    setIsScanning(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const formatDate = (date: string | null) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  return (
    <div className="flex flex-col items-center space-y-6 w-full max-w-sm mx-auto px-4 pb-8">
      {error && (
        <div className="w-full p-4 bg-red-50 rounded-xl">
          <p className="text-sm text-red-500 text-center">{error}</p>
        </div>
      )}

      <div className="w-full space-y-4">
        <AppleButton
          onClick={isScanning ? stopScanning : startScanning}
          variant={isScanning ? "danger" : "heartbeat"}
          className="w-full h-14 text-lg rounded-xl relative"
          disabled={isScanning && !error}
        >
          {isScanning ? (
            <div className="flex items-center space-x-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Abort Scan</span>
            </div>
          ) : (
            "Scan for Tags"
          )}
        </AppleButton>
      </div>

      {isScanning && (
        <div className="text-center space-y-2">
          <div className="w-16 h-16 mx-auto rounded-full bg-blue-50 flex items-center justify-center">
            <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
          </div>
          <p className="text-base text-gray-600">
            Hold your device near an NFC tag
          </p>
        </div>
      )}

      {isLoading && (
        <div className="w-full p-6 bg-gray-50 rounded-xl">
          <div className="flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
          </div>
        </div>
      )}

      {asset && !isScanning && !isLoading && (
        <div className="w-full space-y-6">
          {/* Asset Image */}
          {asset.images && (
            <div className="w-full aspect-square rounded-xl overflow-hidden bg-gray-100">
              <img 
                src={asset.images.split(',')[0]} 
                alt={asset.name || asset.asset_name || 'Asset'} 
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Asset Details */}
          <div className="w-full p-6 bg-white rounded-xl shadow-sm space-y-4">
            <h2 className="text-xl font-semibold">
              {asset.name || asset.asset_name || 'Unnamed Asset'}
            </h2>

            <div className="grid grid-cols-1 gap-4">
              {/* Basic Info */}
              <div className="space-y-3">
                {asset.asset_description && (
                  <p className="text-sm text-gray-600">{asset.asset_description}</p>
                )}

                <div className="flex items-center space-x-2 text-sm">
                  <Tag className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">NFC ID:</span>
                  <span className="font-mono">{asset.nfc_id}</span>
                </div>

                {asset.serial_number && (
                  <div className="flex items-center space-x-2 text-sm">
                    <Box className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Serial Number:</span>
                    <span>{asset.serial_number}</span>
                  </div>
                )}

                {asset.category && (
                  <div className="flex items-center space-x-2 text-sm">
                    <Tag className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Category:</span>
                    <span>{asset.category}</span>
                  </div>
                )}

                {asset.location && (
                  <div className="flex items-center space-x-2 text-sm">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Location:</span>
                    <span>{asset.location}</span>
                  </div>
                )}

                {asset.status && (
                  <div className="flex items-center space-x-2 text-sm">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Status:</span>
                    <span>{asset.status}</span>
                  </div>
                )}

                {asset.notes && (
                  <div className="flex items-start space-x-2 text-sm">
                    <FileText className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <span className="text-gray-600">Notes:</span>
                      <p className="mt-1">{asset.notes}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Asset Records - Only show if see_records is true */}
          {asset.see_records && asset.asset_records && asset.asset_records.length > 0 && (
            <div className="w-full">
              <AssetRecordsList 
                records={asset.asset_records} 
                assetId={asset.id}
                onRecordAdded={() => findAssetByNfcId(nfcId || '')}
                isPublic
              />
            </div>
          )}

          {/* Add Record Button - Only show if add_records is true */}
          {asset.add_records && (
            <div className="w-full">
              <AppleButton
                onClick={() => setShowRecordDialog(true)}
                variant="secondary"
                className="w-full flex items-center justify-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Add Record</span>
              </AppleButton>
            </div>
          )}
        </div>
      )}

      {nfcId && !asset && !isScanning && !isLoading && (
        <div className="w-full p-6 bg-gray-50 rounded-xl space-y-2">
          <p className="text-sm text-gray-500 text-center">NFC ID Scanned:</p>
          <p className="font-mono text-lg text-center text-blue-600">{nfcId}</p>
        </div>
      )}

      <RegisterAssetModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        assetId={nfcId || ''}
        scanType="NFC"
      />

      {/* Record Dialog - Only accessible if add_records is true */}
      <Dialog open={showRecordDialog} onOpenChange={setShowRecordDialog}>
        <DialogContent className="sm:max-w-[600px] h-[90vh] flex flex-col">
          <div className="flex flex-col h-full">
            <DialogHeader>
              <DialogTitle>Create Asset Record</DialogTitle>
              <DialogDescription>
                Add a new record for this asset.
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              {asset && (
                <AssetRecordForm
                  assetId={asset.id}
                  mode="create"
                  onSuccess={() => {
                    setShowRecordDialog(false);
                    findAssetByNfcId(nfcId || '');
                  }}
                  onCancel={() => setShowRecordDialog(false)}
                  isPublic
                />
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
} 